<template>
  <!-- Mobile App Menu Overlay -->
  <div v-if="isOpen" class="mobile-menu-overlay" @click="close"></div>
  <div class="mobile-app-menu" :class="{ open: isOpen }">
    <div class="mobile-menu-header">
      <button class="btn-close-menu" @click="close" aria-label="Close menu">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>
    <div class="mobile-menu-content">
      <!-- Home -->
      <router-link class="mobile-menu-item" :to="{ name: 'home' }" :class="{ active: isActiveRoute('home') }" @click="close">
        <NavIcon name="home" class="me-3" />
        <span>{{ $t('sideMenues.home') }}</span>
      </router-link>

      <!-- Events -->
      <router-link class="mobile-menu-item" :to="{ name: 'events' }" :class="{ active: isActiveRoute('events') || isActiveRoute('events.show') }" @click="close">
        <NavIcon name="events" class="me-3" />
        <span>{{ $t('sideMenues.events') }}</span>
      </router-link>

      <!-- Accommodations -->
      <router-link v-if="canViewDetail" class="mobile-menu-item" :to="{ name: 'accommodation_bookings' }" :class="{ active: isActiveRoute('accommodation_bookings') }" @click="close">
        <NavIcon name="accommodations" class="me-3" />
        <span>{{ $t('sideMenues.accommodationBookings') }}</span>
      </router-link>

      <div v-if="canImport || canExport" class="mobile-menu-section-title">{{ $t('sideMenues.dataManagement') }}</div>

      <!-- Import -->
      <div v-if="canImport" class="mobile-menu-item" @click="toggleImportMenu">
        <i class="bi bi-upload me-3"></i>
        <span>{{ $t('sideMenues.import') }}</span>
        <i class="bi bi-chevron-down ms-auto" :class="{ rotated: isImportMenuOpen }"></i>
      </div>
      <div class="mobile-submenu" :class="{ show: isImportMenuOpen }">
        <router-link class="mobile-submenu-item" :to="{ name: 'import_ledghome' }" :class="{ active: isActiveRoute('import_ledghome') }" @click="close">
          <i class="bi bi-building me-3"></i>
          <span>{{ $t('sideMenues.donatorInformation') }}</span>
        </router-link>
      </div>

      <!-- Export with submenu -->
      <div v-if="canExport" class="mobile-menu-item" @click="toggleExportMenu">
        <i class="bi bi-download me-3"></i>
        <span>{{ $t('sideMenues.export') }}</span>
        <i class="bi bi-chevron-down ms-auto" :class="{ rotated: isExportMenuOpen }"></i>
      </div>
      <div class="mobile-submenu" :class="{ show: isExportMenuOpen }">
        <router-link class="mobile-submenu-item" :to="{ name: 'statistic_data' }" :class="{ active: isActiveRoute('statistic_data') }" @click="close">
          <i class="bi bi-bar-chart me-3"></i>
          <span>{{ $t('sideMenues.statisticData') }}</span>
        </router-link>
      </div>

      <!-- Master Data -->
      <router-link v-if="canEdit" class="mobile-menu-item" :to="{ name: 'master.event_organizers.index' }" :class="{ active: isActiveRoute('master.event_organizers.index') }" @click="close">
        <i class="bi bi-database me-3"></i>
        {{ $t('sideMenues.masterData') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePermission } from '../../../composables/usePermission';
import { colors } from '../../../../consts/colors';
import NavIcon from './NavIcon.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(['close']);

const route = useRoute();
const isImportMenuOpen = ref(false);
const isExportMenuOpen = ref(false);

// Permission checks
const { hasImportPermission, hasExportPermission, hasEditPermission, hasDetailPermission } = usePermission();

// Make permission checks reactive using computed
const canImport = computed(() => hasImportPermission());
const canExport = computed(() => hasExportPermission());
const canEdit = computed(() => hasEditPermission());
const canViewDetail = computed(() => hasDetailPermission());

/**
 * Close menu
 */
const close = () => {
  isImportMenuOpen.value = false;
  isExportMenuOpen.value = false;
  emit('close');
};

/**
 * Toggle import submenu
 */
const toggleImportMenu = () => {
  isImportMenuOpen.value = !isImportMenuOpen.value;
};

/**
 * Toggle export submenu
 */
const toggleExportMenu = () => {
  isExportMenuOpen.value = !isExportMenuOpen.value;
};

/**
 * Check if route is active
 */
const isActiveRoute = (routeName) => {
  return route.name === routeName;
};
</script>

<style lang="scss" scoped>
// Mobile menu overlay
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: v-bind('colors.overlayBg');
  z-index: 1040;
  display: block;
}

// Mobile app menu (slides from right, full content)
.mobile-app-menu {
  position: fixed;
  top: 0;
  right: -100%;
  width: 80%;
  max-width: 320px;
  height: 100vh;
  background-color: v-bind('colors.white');
  box-shadow: -2px 0 8px v-bind('colors.shadowColor');
  transition: right 0.3s ease-in-out;
  z-index: 1050;
  overflow-y: auto;

  &.open {
    right: 0;
  }

  .mobile-menu-header {
    padding: 16px 24px;
    border-bottom: 1px solid v-bind('colors.gray[200]');
    display: flex;
    justify-content: flex-end;

    .btn-close-menu {
      background: none;
      border: none;
      padding: 8px;
      cursor: pointer;
      color: v-bind('colors.textPrimary');
      font-size: 20px;
      transition: color 0.2s;

      &:hover {
        color: v-bind('colors.blue[700]');
      }

      &:focus {
        outline: none;
      }
    }
  }

  .mobile-menu-content {
    padding: 0;
  }

  .mobile-menu-item {
    padding: 16px 24px;
    display: flex;
    align-items: center;
    color: v-bind('colors.textPrimary');
    font-size: 14px;
    font-weight: 400;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 1px solid v-bind('colors.background');

    &:hover:not(.disabled) {
      background-color: v-bind('colors.btnBgColorHover');
      color: v-bind('colors.btnTextColorHover');
    }

    &.active {
      background-color: v-bind('colors.btnBgColorPrimary');
      color: v-bind('colors.btnTextColorPrimary');
      font-weight: 600;
    }

    &.disabled {
      color: v-bind('colors.gray[400]');
      cursor: not-allowed;
      opacity: 0.6;
    }

    i {
      font-size: 16px;
    }

    .bi-chevron-down {
      font-size: 12px;
      transition: transform 0.2s;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .mobile-menu-section-title {
    padding: 20px 24px 8px 24px;
    color: v-bind('colors.gray[500]');
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .mobile-submenu {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
    background-color: v-bind('colors.gray[50]');

    &.show {
      max-height: 300px;
    }
  }

  .mobile-submenu-item {
    padding: 12px 24px 12px 48px;
    display: flex;
    align-items: center;
    color: v-bind('colors.textPrimary');
    font-size: 14px;
    text-decoration: none;
    transition: all 0.2s;

    &:hover:not(.disabled) {
      background-color: v-bind('colors.gray[200]');
      color: v-bind('colors.btnTextColorHover');
    }

    &.active {
      background-color: v-bind('colors.btnBgColorPrimary');
      color: v-bind('colors.btnTextColorPrimary');
      font-weight: 600;
    }

    &.disabled {
      color: v-bind('colors.gray[400]');
      cursor: not-allowed;
      opacity: 0.6;
    }

    i {
      font-size: 14px;
    }
  }
}
</style>
